import { Box, Dialog, Grid, GridItem, useMediaQuery, useToken } from '@chakra-ui/react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { FaCircleCheck, FaCircleXmark, FaTriangleExclamation } from 'react-icons/fa6';
import { useSearchParams } from 'react-router';
import FaceliftButton from '@/components/Atoms/FaceliftButton';
import FaceliftDialog from '@/components/Atoms/FaceliftDialog';
import FaceliftOverlayLoader from '@/components/Atoms/FaceliftOverlayLoader/FaceliftOverlayLoader';
import type { SelectItem } from '@/components/Atoms/FaceliftSelect/FaceliftSelect.types';
import FaceliftText from '@/components/Atoms/FaceliftText';
import { FaceliftPdfViewerContainer } from '@/components/Molecules/FaceliftPdfViewerContainer/FaceliftPdfViewerContainer';
import { PaymentLinkBox } from '@/components/Molecules/PaymentLinkBox';
import Show from '@/components/Molecules/Show/Show';
import CheckoutInvoiceSummary from '@/components/Organisms/CheckoutInvoiceSummary';
import InvoiceCartList from '@/components/Organisms/InvoiceCartList';
import {
	useGetQuickbooksInvoiceData,
	useGetStripeCheckoutSession,
	useSubmitPaymentReportOnSuccess,
} from '@/hooks/service-hooks/stripe';
import { useInvoicesTutorial } from '@/hooks/tutorialsHooks/useInvoicesTutorial';
import { useTutorialActive } from '@/hooks/tutorialsHooks/useTutorialActive';
import { useAppTour } from '@/hooks/useAppTour';
import { useInvoiceCart } from '@/hooks/useInvoiceCart';
import { useInvoicePDFViewer } from '@/hooks/useInvoicePDFViewer';
import { useAuthStore, useClinicStore } from '@/store';
import { useTourContext } from '@/store/tourContext';
import type { InvoiceCheckoutData } from '@/types/invoice';
import type { QuickBooksInvoice } from '@/types/quickbooks';
import { trackCoreClick } from '@/utils/analytics';
import { generateMockInvoiceData } from '@/utils/tutorials/mockData/invoicesMockData';

const PAYMENT_METHOD_MAP: Record<string, 'CREDIT_CARD' | 'BANK_ACCOUNT'> = {
	card: 'CREDIT_CARD',
	ach: 'BANK_ACCOUNT',
};

const SURCHARGE_EXEMPT_STATES = ['MA', 'MASSACHUSETTS', 'PR', 'PUERTO RICO', 'NY', 'NEW YORK'];
const SURCHARGE_RATE_2_STATES = ['CO', 'COLORADO', 'OK', 'OKLAHOMA'];
const CREDIT_CARD_SURCHARGE_RATE = 0.03; // 3%
const CREDIT_CARD_SURCHARGE_RATE_2 = 0.02; // 2%

const InvoiceCartView = () => {
	const { cartedInvoices, clearInvoicesFromCart, addInvoiceToCart } =
		useInvoiceCart();
	const { user } = useAuthStore();
	const [isBelow930] = useMediaQuery(['(max-width: 930px)']);

	// Tour context for tutorial detection
	const { 
		isAnyInvoiceOrOnboardingTutorialActive: isAnyInvoiceTutorialActive,
	} = useTutorialActive();

	// Refs for tutorial
	const invoicesTableRef = useRef<HTMLDivElement | null>(null);

	// Get user clinics from store
	const storeClinics = useClinicStore((state) => state.currentClinics);
	// Get all available clinics for surcharge bypass lookup (invoices can come from any assigned clinic, not just preferred)
	const rawClinics = useClinicStore((state) => state.rawClinics);
	const clinicItems: SelectItem[] = useMemo(() => {
		return storeClinics.map((clinic) => ({
			value: clinic.accountid,
			label: clinic.name,
		}));
	}, [storeClinics]);

	// Helper to check if a clinic has surcharge bypass enabled in CRM
	const isClinicBypassSurcharge = useCallback((clinicGUID: string): boolean => {
		const cleanGUID = clinicGUID?.replace(/[{}]/g, '');
		const clinic = rawClinics.find(
			(c) => c.accountid?.toLowerCase() === cleanGUID?.toLowerCase()
		);
		return clinic?.ta_bypasssurcharges === true;
	}, [rawClinics]);

	const [expandedInvoices, setExpandedInvoices] = useState<
		InvoiceCheckoutData[]
	>([]);
	const [openPaymentLinkModal, setOpenPaymentLinkModal] =
		useState<boolean>(false);
	const [openErrorModal, setOpenErrorModal] = useState<boolean>(false);
	const [openPaymentSuccessModal, setOpenPaymentSuccessModal] =
		useState<boolean>(false);
	const [errorMessage, setErrorMessage] = useState<string>('');
	const [openConfirmationModal, setOpenConfirmationModal] =
		useState<boolean>(false);
	const [pendingCheckoutType, setPendingCheckoutType] = useState<
		'session' | 'link'
	>('session');
	const [paymentMethod, setPaymentMethod] = useState<string>('ach');
	const [openCreditCardDialog, setOpenCreditCardDialog] = useState<boolean>(false);
	const [creditCardSurchargeApplied, setCreditCardSurchargeApplied] = useState<boolean>(false);

	const shouldFetchQBDataRef = useRef<boolean>(true);
	const hasProcessedPaymentRef = useRef<boolean>(false);

	// Refs for tutorial
	const checkoutButtonRef = useRef<HTMLButtonElement | null>(null);
	const confirmButtonRef = useRef<HTMLButtonElement | null>(null);
	const stripeDisclaimerRef = useRef<HTMLElement | null>(null);

	// Register tutorial actions and handle tutorial logic
	useInvoicesTutorial({
		invoicesTableRef,
		openConfirmationModal: () => setOpenConfirmationModal(true),
		expandedInvoicesCount: expandedInvoices.length,
		openConfirmationModalState: openConfirmationModal,
		stripeDisclaimerRef,
		confirmButtonRef,
		checkoutButtonRef,
	});

	// Use PDF viewer hook for invoice viewing
	const {
		viewInvoiceModal,
		setViewInvoiceModal,
		pdfViewerUrl,
		isFetchingPDF,
		isEmptyOrUnavailable,
		isPdfLoadError,
		setSelectedInvoiceNumber,
		setSelectedClinicQBOId,
		resetPDFViewer,
	} = useInvoicePDFViewer();


	const [brand600] = useToken('colors', ['brand.600']);

	const [searchParams, setSearchParams] = useSearchParams();
	const sessionId = searchParams.get('session_id');

	// Auto-open modal when navigating from tutorial action with URL parameter
	useEffect(() => {
		const openStripeModal = searchParams.get('openStripeModal');
		if (
			isAnyInvoiceTutorialActive &&
			openStripeModal === 'true' &&
			!openConfirmationModal &&
			cartedInvoices.length > 0
		) {
			// Small delay to ensure component is fully mounted and expandedInvoices are set
			setTimeout(() => {
				setOpenConfirmationModal(true);
				// Remove parameter from URL
				const newSearchParams = new URLSearchParams(searchParams);
				newSearchParams.delete('openStripeModal');
				setSearchParams(newSearchParams, { replace: true });
			}, 500);
		}
	}, [
		isAnyInvoiceTutorialActive,
		openConfirmationModal,
		searchParams,
		setSearchParams,
		cartedInvoices.length,
	]);
	const paymentLogId = searchParams.get('payment_log_id');
	const otp = searchParams.get('otp');

	const hasPaymentParams = Boolean(sessionId && paymentLogId && otp);

	// Prepare QuickBooks invoice request payload
	const qboInvoiceRequestPayload = useMemo(
		() =>
			cartedInvoices.map((invoice) => ({
				clinicQBOId: invoice.clinicQBOId ?? 'N/A',
				invoiceID: invoice.InvoiceID,
			})),
		[cartedInvoices],
	);

	// Prepare Stripe checkout session request payload
	const stripeCheckoutRequestPayload = useMemo(
		() => ({
			invoiceRequestData: cartedInvoices.map((invoice) => ({
				clinicQBOId: invoice.clinicQBOId ?? 'N/A',
				invoiceNumber: invoice.Invoice,
				clinicGUID: invoice.Account_ID,
				clinicState: invoice.State ?? '',
				bypassSurcharges: isClinicBypassSurcharge(invoice.Account_ID),
			})),
			userData: {
				email: user?.userInformation?.email || '',
				name: `${user?.userInformation?.firstName} ${user?.userInformation?.lastName}`,
			},
			stripeSessionType: 'session', // Default value, can be changed as needed
			userSelectedClinics: clinicItems.map((clinic) => clinic.label).join('||'),
		}),
		[cartedInvoices, user, clinicItems, isClinicBypassSurcharge],
	);

	// Fetch QuickBooks invoice data (only once when cart has items)
	// Disable fetching when tutorial is active (we use mock data)
	const { data: quickbooksData, isLoading: isLoadingQuickbooks } =
		useGetQuickbooksInvoiceData(
			qboInvoiceRequestPayload,
			shouldFetchQBDataRef.current &&
				cartedInvoices.length > 0 &&
				!isAnyInvoiceTutorialActive,
		);

	// Disable fetching after first successful fetch
	useEffect(() => {
		if (quickbooksData?.items && shouldFetchQBDataRef.current) {
			shouldFetchQBDataRef.current = false;
		}
	}, [quickbooksData]);

	// Derive totals from expandedInvoices
	const { orderTotal, subtotal, totalTax, shippingCost, surcharge } = useMemo(() => {
		const totals = expandedInvoices.reduce(
			(acc, item) => ({
				balance: acc.balance + (item.updatedInvoiceAmount || 0),
				tax: acc.tax + (item.tax || 0),
				shipping: acc.shipping + (item.shippingCost || 0),
			}),
			{ balance: 0, tax: 0, shipping: 0 },
		);

		const baseTotal = totals.balance;
		let calculatedSurcharge = 0;

		// Only apply surcharge when Credit Card is selected AND surcharge is accepted
		if (creditCardSurchargeApplied && paymentMethod === 'card' && !isAnyInvoiceTutorialActive) {
			// Calculate surcharge separately for 2% states and other non-exempt states (3%)
			let sc2Amount = 0;
			let otherStatesAmount = 0;

			expandedInvoices.forEach((item) => {
				const state = item.State?.toUpperCase() || '';
				const isExempt = SURCHARGE_EXEMPT_STATES.includes(state);
				const isBypass = isClinicBypassSurcharge(item.Account_ID);

				if (!isExempt && !isBypass && item.updatedInvoiceAmount && item.updatedInvoiceAmount > 0) {
					if (SURCHARGE_RATE_2_STATES.includes(state)) {
						sc2Amount += item.updatedInvoiceAmount;
					} else {
						otherStatesAmount += item.updatedInvoiceAmount;
					}
				}
			});

			const surcharge2Amount = Math.round(sc2Amount * CREDIT_CARD_SURCHARGE_RATE_2 * 100) / 100;
			const otherStatesSurcharge = Math.round(otherStatesAmount * CREDIT_CARD_SURCHARGE_RATE * 100) / 100;
			calculatedSurcharge = surcharge2Amount + otherStatesSurcharge;
		}

		const orderTotalWithSurcharge = baseTotal + calculatedSurcharge;

		return {
			orderTotal: orderTotalWithSurcharge,
			totalTax: totals.tax,
			shippingCost: totals.shipping,
			subtotal: totals.balance - totals.tax - totals.shipping,
			surcharge: calculatedSurcharge,
		};
	}, [expandedInvoices, creditCardSurchargeApplied, paymentMethod, isAnyInvoiceTutorialActive, isClinicBypassSurcharge]);

	// Mutations for create and update
	const {
		mutateAsync: createStripeSession,
		data: stripeSessionData,
		isLoading: isStripeSessionLoading,
	} = useGetStripeCheckoutSession(
		(data) => {
			// this will work on success callback
			if (data.stripeSessionType === 'link') {
				setOpenPaymentLinkModal(true);
			} else {
				window.location.href = data?.url || '/';
			}
		},
		(error: string) => {
			console.error('Stripe session error:', error);
			setErrorMessage(error);
			setOpenErrorModal(true);
		},
	);

	// Submit payment report on success
	const {
		isLoading: isPaymentSuccessLoading,
		isSuccess: isPaymentSuccessExecuted,
		isError: isPaymentSuccessFailed,
	} = useSubmitPaymentReportOnSuccess(
		{
			session_id: sessionId || '',
			otp: otp || '',
			payment_log_id: paymentLogId || '',
		},
		hasPaymentParams,
		(error: string) => {
			console.error('Payment report error:', error);
			setErrorMessage(error);
			setOpenErrorModal(true);
		},
	);

	const onCheckout = (stripeSessionType: 'session' | 'link') => {
		trackCoreClick(
			stripeSessionType === 'session'
				? 'pay_invoice_clicked'
				: 'generate_payment_link_clicked',
			{
				source_module: 'invoice_cart',
				checkout_type: stripeSessionType,
				payment_method: paymentMethod,
				invoice_count: expandedInvoices.length,
				invoice_numbers: expandedInvoices.map((invoice) => invoice.Invoice),
				order_total: orderTotal,
			},
		);

		if (stripeSessionType === 'session') {
			setPendingCheckoutType(stripeSessionType);
			setOpenConfirmationModal(true);
		} else {
			handleConfirmCheckout(stripeSessionType);
		}
	};

	const handleConfirmCheckout = (stripeSessionType: 'session' | 'link') => {
		setOpenConfirmationModal(false);
		createStripeSession({
			...stripeCheckoutRequestPayload,
			stripeSessionType: stripeSessionType,
			paymentMethod: PAYMENT_METHOD_MAP[paymentMethod] || 'CREDIT_CARD',
		});
	};

	const handleConfirmCheckoutClick = () => {
		trackCoreClick('confirm_checkout_clicked', {
			source_module: 'invoice_cart',
			checkout_type: pendingCheckoutType,
			payment_method: paymentMethod,
			invoice_count: expandedInvoices.length,
			invoice_numbers: expandedInvoices.map((invoice) => invoice.Invoice),
			order_total: orderTotal,
		});
		handleConfirmCheckout(pendingCheckoutType);
	};

	const handleCopyPaymentLink = () => {
		trackCoreClick('copy_payment_link_clicked', {
			source_module: 'invoice_cart',
			checkout_type: 'link',
			invoice_count: expandedInvoices.length,
			invoice_numbers: expandedInvoices.map((invoice) => invoice.Invoice),
		});
	};

	// Handle post-payment success
	useEffect(() => {
		if (!hasPaymentParams || hasProcessedPaymentRef.current) return;

		hasProcessedPaymentRef.current = true;
		setOpenPaymentSuccessModal(true);
		clearInvoicesFromCart();
		// Totals will automatically reset to 0 when expandedInvoices becomes empty
	}, [hasPaymentParams, clearInvoicesFromCart]);

	// Simulate invoices for tutorial when active (only if cart is empty)
	// This ensures invoices are added only once when first navigating to /invoice-cart
	useEffect(() => {
		if (isAnyInvoiceTutorialActive && cartedInvoices.length === 0) {
			// Add 3 mock invoices to cart for demo only if cart is empty
			const mockInvoices = generateMockInvoiceData(3);
			mockInvoices.forEach((invoice) => {
				addInvoiceToCart(invoice);
			});
		}
	}, [isAnyInvoiceTutorialActive, cartedInvoices.length, addInvoiceToCart]);

	// Tutorial actions are now handled by useInvoicesTutorial hook above

	// Process QuickBooks invoice data when available
	useEffect(() => {
		if (!cartedInvoices.length) {
			setExpandedInvoices([]);
			return;
		}

		// For tutorial, use mock data directly
		if (isAnyInvoiceTutorialActive) {
			const mockExpandedInvoices: InvoiceCheckoutData[] = cartedInvoices.map(
				(invoice) => ({
					...invoice,
					updatedInvoiceAmount: invoice.QBAmtDue || 1000,
					tax: (invoice.QBAmtDue || 1000) * 0.08, // 8% tax
					shippingCost: 0,
				}),
			);
			setExpandedInvoices(mockExpandedInvoices);
			return;
		}

		// if (!quickbooksData?.items) {
		// 	return;
		// }

		const invoiceDetailsMap = new Map<
			string,
			{ updatedInvoiceAmount: number; tax: number; shippingCost: number }
		>();

		quickbooksData?.items?.forEach((invoice: QuickBooksInvoice) => {
			const taxAmount = invoice.TxnTaxDetail?.TotalTax ?? 0;
			const hasFedExShipping = invoice.Line?.some((line) =>
				line.Description?.toLowerCase().includes('fedex'),
			);
			const key = `${invoice.DocNumber}|${invoice.CustomerRef?.value ?? ''}`;

			invoiceDetailsMap.set(key, {
				updatedInvoiceAmount: invoice.Balance ?? 0,
				tax: taxAmount,
				shippingCost: hasFedExShipping ? taxAmount : 0,
			});
		});

		const updatedInvoices = cartedInvoices.map((invoice) => {
			const key = `${invoice.Invoice}|${invoice.clinicQBOId ?? 'N/A'}`;
			const details = invoiceDetailsMap.get(key) ?? {
				updatedInvoiceAmount: 0,
				tax: 0,
				shippingCost: 0,
			};

			return {
				...invoice,
				...details,
			};
		});

		setExpandedInvoices(
			updatedInvoices.sort((a, b) => a.Invoice.localeCompare(b.Invoice)),
		);
	}, [cartedInvoices, quickbooksData, isAnyInvoiceTutorialActive]);

	/**
	 * Handle viewing invoice PDF in modal
	 * Sets the invoice number and clinic QBO ID to trigger the PDF fetch
	 */
	const handleViewInvoice = useCallback(
		(invoiceNumber: string, clinicQBOId: string) => {
			if (!clinicQBOId) {
				toast.error('Clinic QBO ID not found for the selected invoice');
				return;
			}

			// Set invoice details to trigger the PDF fetch via useInvoicePDFViewer hook
			setSelectedInvoiceNumber(invoiceNumber);
			setSelectedClinicQBOId(clinicQBOId);
			setViewInvoiceModal(true);
		},
		[setSelectedInvoiceNumber, setSelectedClinicQBOId, setViewInvoiceModal],
	);

	// Register tour elements
	useAppTour({
		elements: {
			InvoicesCartTrashIconRef: null, // Will be registered dynamically
			InvoicesCartCheckoutButtonRef: checkoutButtonRef.current,
			InvoicesStripeDisclaimerRef: stripeDisclaimerRef.current,
			InvoicesConfirmButtonRef: confirmButtonRef.current,
		},
	});

	// Ref registration is now handled by useInvoicesTutorial hook

	// Helper function to check if any invoices are from non-exempt states and non-bypass clinics (surchargeable)
	const hasSurchargeableInvoices = useCallback(() => {
		return expandedInvoices?.some((item) => {
			const state = item.State?.toUpperCase() || '';
			const isExempt = SURCHARGE_EXEMPT_STATES.includes(state);
			const isBypass = isClinicBypassSurcharge(item.Account_ID);
			return !isExempt && !isBypass && (item.updatedInvoiceAmount ?? 0) > 0;
		}) || false;
	}, [expandedInvoices, isClinicBypassSurcharge]);

	// Show credit card surcharge dialog when credit card is selected AND there are surchargeable invoices
	useEffect(() => {
		if (paymentMethod === 'card' && !creditCardSurchargeApplied) {
			if (hasSurchargeableInvoices()) {
				setOpenCreditCardDialog(true);
			} else {
				setCreditCardSurchargeApplied(true);
			}
		}
	}, [paymentMethod, creditCardSurchargeApplied, hasSurchargeableInvoices]);

	// Reset surcharge when payment method changes away from credit card
	useEffect(() => {
		if (paymentMethod !== 'card') {
			setCreditCardSurchargeApplied(false);
		}
	}, [paymentMethod]);

	return (
		<>
			<style>
				{`
					@keyframes fadeInScale {
						0% { opacity: 0; transform: scale(0.8); }
						50% { transform: scale(1.05); }
						100% { opacity: 1; transform: scale(1); }
					}
					@keyframes bounceIn {
						0% { transform: scale(0); }
						50% { transform: scale(1.2); }
						100% { transform: scale(1); }
					}
					@keyframes fadeInUp {
						0% { opacity: 0; transform: translateY(-10px); }
						100% { opacity: 1; transform: translateY(0); }
					}
					@keyframes shakeIn {
						0% { opacity: 0; transform: translateX(-20px); }
						25% { transform: translateX(10px); }
						50% { transform: translateX(-10px); }
						75% { transform: translateX(5px); }
						100% { opacity: 1; transform: translateX(0); }
					}
					@keyframes pulse {
						0%, 100% { transform: scale(1); }
						50% { transform: scale(1.1); }
					}
					.animate-fadeInScale {
						animation: fadeInScale 0.5s ease-out;
					}
					.animate-bounceIn {
						animation: bounceIn 0.6s cubic-bezier(0.68, -0.55, 0.265, 1.55);
					}
					.animate-fadeInUp {
						animation: fadeInUp 0.3s ease-out;
					}
					.animate-shakeIn {
						animation: shakeIn 0.5s ease-out;
					}
					.animate-pulse {
						animation: pulse 0.6s ease-in-out;
					}
				`}
			</style>
			<Box p={{ base: 4, md: 8 }}>
				<Show when={isStripeSessionLoading}>
					<FaceliftOverlayLoader />
				</Show>

			<Grid templateColumns={{ base: '1fr', md: 'repeat(3, 1fr)' }} gap={6}>
				<GridItem colSpan={{ base: 1, md: 2 }}>
					<InvoiceCartList
						invoices={expandedInvoices}
						isLoading={isLoadingQuickbooks && !isAnyInvoiceTutorialActive}
						handleViewInvoice={handleViewInvoice}
					/>
				</GridItem>
				<GridItem colSpan={1} display={{ base: 'none', md: 'block' }}>
					<CheckoutInvoiceSummary
						subtotal={subtotal}
						tax={totalTax}
						shipping={shippingCost}
						total={orderTotal}
						surcharge={surcharge}
						onCheckout={onCheckout}
						isStripeSessionLoading={isStripeSessionLoading}
						checkoutButtonRef={checkoutButtonRef}
						paymentMethod={paymentMethod}
						onPaymentMethodChange={setPaymentMethod}
					/>
				</GridItem>
			</Grid>
			{/* Show summary below list on small screens */}
			<Box display={{ base: 'block', md: 'none' }} mt={6}>
				<CheckoutInvoiceSummary
					subtotal={subtotal}
					tax={totalTax}
					shipping={shippingCost}
					total={orderTotal}
					surcharge={surcharge}
					onCheckout={onCheckout}
					isStripeSessionLoading={isStripeSessionLoading}
					checkoutButtonRef={checkoutButtonRef}
					paymentMethod={paymentMethod}
					onPaymentMethodChange={setPaymentMethod}
				/>
			</Box>

			{/* Payment Link Modal */}
			<FaceliftDialog
				showBackBtn={true}
				open={openPaymentLinkModal}
				onOpenChange={() => setOpenPaymentLinkModal(false)}
				title="💳 Stripe Payment Link"
				size="xl"
				sx={{ boxShadow: `0 0 20px 0px ${brand600}` }}
				contentProps={isBelow930 ? {
					mx: { base: 3, md: 'auto' },
					maxW: { base: '95vw', sm: '80vw', md: '83vw', lg: '70vw' },
				} : {}}
			>
				<Box ml={3}>
					<FaceliftText mb={4}>
						Share this link with your customer to complete the payment:
					</FaceliftText>
					<PaymentLinkBox
						link={stripeSessionData?.url || ''}
						onCopyLink={handleCopyPaymentLink}
					/>
				</Box>
			</FaceliftDialog>

			{/* Error Modal */}
			<FaceliftDialog
				open={openErrorModal}
				onOpenChange={() => setOpenErrorModal(false)}
				title="We Couldn't Start Your Payment"
				size="md"
			>
				<Box ml={3}>
					<FaceliftText mb={4} color="red.500">
						Sorry, we couldn't start your payment. Please try again.
					</FaceliftText>
					{errorMessage && (
						<FaceliftText fontSize="sm" color="gray.500">
							{errorMessage}
						</FaceliftText>
					)}
				</Box>
			</FaceliftDialog>

		{/* Payment Success Modal */}
		<FaceliftDialog
			open={openPaymentSuccessModal}
			onOpenChange={() =>
				setOpenPaymentSuccessModal(!openPaymentSuccessModal)
			}
			title={isPaymentSuccessExecuted ? "" : isPaymentSuccessFailed ? "Payment Failed" : "Processing Payment"}
			size="md"
			sx={{ boxShadow: `0 0 20px 0px ${brand600}` }}
		>	
			<Show when={isPaymentSuccessLoading}>
				<Box textAlign="center" py={6}>
					<FaceliftText mb={4} fontSize="lg" fontWeight="500">
						Please wait, processing your payment...
					</FaceliftText>
					<FaceliftOverlayLoader />
				</Box>
			</Show>
			<Show when={isPaymentSuccessExecuted}>
				<Box 
					className="animate-fadeInScale"
					textAlign="center" 
					py={8} 
					px={4}
				>
					<Box
						className="animate-bounceIn"
						display="inline-block"
					>
						<Box
							as={FaCircleCheck}
							fontSize="6xl"
							color="green.500"
							mb={4}
						/>
					</Box>
					
					<FaceliftText 
						fontSize="2xl" 
						fontWeight="700" 
						mb={3}
						color="green.600"
						_dark={{ color: 'green.400' }}
					>
						Payment Successful!
					</FaceliftText>
					
				<FaceliftText 
					fontSize="md" 
					color="gray.600"
					_dark={{ color: 'gray.300' }}
					mb={6}
				>
					Your payment has been processed successfully.
					<br />
					Thank you for your business!
				</FaceliftText>
				</Box>
			</Show>
		<Show when={isPaymentSuccessFailed}>
			<Box 
				className="animate-shakeIn"
				textAlign="center" 
				py={8} 
				px={4}
			>
				<Box
					className="animate-pulse"
					display="inline-block"
				>
					<Box
						as={FaCircleXmark}
						fontSize="6xl"
						color="red.500"
						mb={4}
					/>
				</Box>
				
				<FaceliftText 
					fontSize="2xl" 
					fontWeight="700" 
					mb={2}
					color="red.600"
					_dark={{ color: 'red.400' }}
				>
					Payment Failed
				</FaceliftText>
				
				<FaceliftText 
					fontSize="md" 
					color="gray.600"
					_dark={{ color: 'gray.300' }}
					mb={6}
				>
					We couldn't process your payment at this time.
				</FaceliftText>

				{errorMessage && (
					<Box
						bg="red.50"
						_dark={{ bg: 'red.900', borderColor: 'red.700' }}
						borderRadius="lg"
						p={4}
						mb={6}
						border="1px solid"
						borderColor="red.200"
					>
					<Box display="flex" alignItems="flex-start" gap={3} textAlign="left">
						<Box as={FaTriangleExclamation} fontSize="lg" color="red.600" _dark={{ color: 'red.400' }} mt={0.5} flexShrink={0} />
							<Box>
								<FaceliftText fontSize="sm" fontWeight="600" color="red.800" _dark={{ color: 'red.300' }} mb={1}>
									Error Details
								</FaceliftText>
								<FaceliftText fontSize="sm" color="red.700" _dark={{ color: 'red.200' }}>
									{errorMessage}
								</FaceliftText>
							</Box>
						</Box>
					</Box>
				)}
			</Box>
		</Show>
		</FaceliftDialog>

			{/* This Dialog is used to view the selected invoice PDF */}
			<FaceliftDialog
				open={viewInvoiceModal}
				showCloseBtn={true}
				onOpenChange={(isOpen) => {
					setViewInvoiceModal(isOpen);
					// Clear state when modal closes
					if (!isOpen) {
						resetPDFViewer();
					}
				}}
				title="View Invoice"
				size="xl"
				sx={{ boxShadow: `0 0 20px 0px ${brand600}` }}
			>
				<FaceliftPdfViewerContainer
					isLoading={isFetchingPDF}
					url={pdfViewerUrl}
					isEmptyOrUnavailable={isEmptyOrUnavailable}
					isLoadError={isPdfLoadError}
				/>
			</FaceliftDialog>

			{/* Confirmation Modal for Stripe Redirect */}
			<FaceliftDialog
				showBackBtn={true}
				open={openConfirmationModal}
				onOpenChange={setOpenConfirmationModal}
				title="Redirecting to Stripe Online"
				size="xl"
				sx={{
					boxShadow: `0 0 20px 0px ${brand600}`,
				}}
				contentProps={isBelow930 ? {
					mx: { base: 3, md: 'auto' },
					maxW: { base: '95vw', sm: '80vw', md: '83vw', lg: '70vw' },
				} : {}}
			>
					<Box px={3}>
						<FaceliftText fontSize="lg" mb={6}>
							You are being redirected to Stripe official site for a secure
							payment.
						</FaceliftText>

						<Box
							ref={stripeDisclaimerRef}
							mb={6}
							boxShadow="sm"
							border="1px solid"
							borderColor="orange.300"
							bg="orange.50"
							_dark={{
								borderColor: 'orange.600',
								bg: 'orange.900',
							}}
							p={4}
							borderRadius="md"
						>
							<Box display="flex" alignItems="flex-start" gap={2} mb={4}>
								<Box as={FaTriangleExclamation} fontSize="xl" mt={1} />
								<FaceliftText fontWeight="600" fontSize="md" fontStyle="italic">
									Important Notes:
								</FaceliftText>
							</Box>

							<Box as="ul" pl={6} spaceY={3}>
								<Box as="li" mb={3}>
									<FaceliftText fontSize="sm">
										Please ensure these are the invoices you'd like to pay, as
										they will be locked for 30 minutes to prevent duplicate
										payments once directed to Stripe.
									</FaceliftText>
								</Box>
								<Box as="li">
									<FaceliftText fontSize="sm">
										This payment session on Stripe will expire after 24 hours if
										left open and not finalized. Once expired, you'll need to
										refill your Shopping Cart with the desired invoices and
										Proceed To Checkout once again.
									</FaceliftText>
								</Box>
							</Box>
						</Box>

						<Box display="flex" gap={4} justifyContent="flex-end">
							<FaceliftButton
								variant="outline"
								outline={'none'}
								colorScheme="gray"
								onClick={() => setOpenConfirmationModal(false)}
								cursor="pointer"
								width={'32'}
								bg="transparent"
								color="inherit"
								border="1px solid"
							>
								CANCEL
							</FaceliftButton>
							<FaceliftButton
								ref={confirmButtonRef}
								outline={'none'}
								colorScheme="orange"
								onClick={handleConfirmCheckoutClick}
								cursor="pointer"
								width={'32'}
							>
								CONFIRM
							</FaceliftButton>
						</Box>
					</Box>
			</FaceliftDialog>

			{/* Credit Card Surcharge Notice Dialog */}
			<FaceliftDialog
				open={openCreditCardDialog}
				onOpenChange={(isOpen) => {
					setOpenCreditCardDialog(isOpen);
					// If dialog is closed without applying, revert to ACH
					if (!isOpen && paymentMethod === 'card' && !creditCardSurchargeApplied) {
						setPaymentMethod('ach');
						setCreditCardSurchargeApplied(false);
					}
				}}
				title="Credit Card Surcharge Notice"
				size="md"
				sx={{
					boxShadow: `0 0 20px 0px ${brand600}`,
					borderRadius: '12px',
				}}
				footer={
					<>
						<FaceliftButton
							width="100px"
							bg="transparent"
							color={{ _light: 'gray.600', _dark: 'gray.100' }}
							border="1px solid"
							borderColor={{ _light: 'gray.600', _dark: 'gray.100' }}
							onClick={() => {
								setOpenCreditCardDialog(false);
								// Revert to ACH when declining
								setPaymentMethod('ach');
								setCreditCardSurchargeApplied(false);
							}}
						>
							No
						</FaceliftButton>
						<FaceliftButton
							width="100px"
							onClick={() => {
								setCreditCardSurchargeApplied(true);
								setOpenCreditCardDialog(false);
							}}
						>
							Yes, I accept
						</FaceliftButton>
					</>
				}
			>
				<Box ml={3}>
					<FaceliftText mb={1}>
						Credit card payments are subject to a surcharge fee at checkout, where permitted by law. The surcharge applies only to invoices from eligible states, and certain clinics may be exempt.
					</FaceliftText>
				</Box>
			</FaceliftDialog>
			</Box>
		</>
	);
};

export default InvoiceCartView;
